import type { EstimateRecurringConfig } from '@/types/estimate';
import { parseDateOnly } from '@/lib/dates/date-only';

const FREQUENCY_LABEL: Record<string, string> = {
  WEEKLY: 'weekly',
  BIWEEKLY: 'every 2 weeks',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  ANNUALLY: 'yearly',
};

export function formatRecurringDate(value: string): string {
  // eslint-disable-next-line no-restricted-syntax -- parseDateOnly returns a local-midnight Date for a date-only value; local rendering IS the intended calendar day (no tz instant involved).
  return parseDateOnly(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// NOT-224: noun-style frequency labels for the "Recurring service" block.
const FREQUENCY_NOUN: Record<string, string> = {
  DAILY: 'Daily visits',
  WEEKLY: 'Weekly visits',
  BIWEEKLY: 'Every 2 weeks',
  MONTHLY: 'Monthly visits',
  QUARTERLY: 'Quarterly visits',
  SEMIANNUAL: 'Every 6 months',
  ANNUAL: 'Yearly visits',
  ANNUALLY: 'Yearly visits',
};

export interface RecurringParts {
  frequency: string;
  start?: string;
  duration: string;
  details: Array<{ label: string; value: string }>;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function money(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
}

/**
 * NOT-224: structured parts for the customer-facing "Recurring service" block.
 * Covers every frequency/duration combo incl. CUSTOM and the indefinite case
 * ("Until cancelled"), which `formatRecurringSummary` omits.
 */
export function formatRecurringParts(config: EstimateRecurringConfig): RecurringParts {
  const frequency =
    config.frequency === 'CUSTOM' && config.interval && config.intervalUnit
      ? `Every ${config.interval} ${String(config.intervalUnit).toLowerCase()}`
      : (FREQUENCY_NOUN[config.frequency] ?? `${String(config.frequency).toLowerCase()} visits`);

  const start = config.startDate ? `Starting ${formatRecurringDate(config.startDate)}` : undefined;

  const duration =
    config.endType === 'AFTER_COUNT' && config.endAfterCount
      ? `${config.endAfterCount} visit${config.endAfterCount === 1 ? '' : 's'} total`
      : config.endType === 'ON_DATE' && config.endOnDate
        ? `Until ${formatRecurringDate(config.endOnDate)}`
        : 'Until cancelled';

  const details: Array<{ label: string; value: string }> = [
    { label: 'Frequency', value: frequency },
    ...(start ? [{ label: 'Start date', value: start.replace('Starting ', '') }] : []),
    { label: 'Duration', value: duration },
  ];

  if (config.customDays?.length) {
    details.push({
      label: 'Service days',
      value: config.customDays.map((day) => WEEKDAYS[day] ?? String(day)).join(', '),
    });
  }
  if (config.visitGranularity === 'DAY_FLEXIBLE') {
    details.push({ label: 'Arrival', value: 'Flexible during the service day' });
  } else if (config.visitStartTime) {
    details.push({
      label: 'Visit time',
      value: config.visitEndTime
        ? `${config.visitStartTime}–${config.visitEndTime}`
        : config.visitStartTime,
    });
  }

  const mode = config.billingMode ?? 'PER_VISIT';
  const currency = config.currency ?? 'USD';
  if (mode === 'SUBSCRIPTION') {
    const cadence = config.billingCadence;
    const cadenceUnit = cadence
      ? { MONTHLY: 'month', QUARTERLY: 'quarter', ANNUALLY: 'year' }[cadence.freq]
      : null;
    const cadenceLabel = cadenceUnit
      ? cadence!.interval > 1
        ? `${cadence!.interval} ${cadenceUnit}s`
        : cadenceUnit
      : 'billing period';
    details.push({
      label: 'Billing',
      value: `${money(Number(config.baseAmount ?? 0), currency)} per ${cadenceLabel}`,
    });
  } else if (mode === 'PREPAID') {
    details.push({
      label: 'Billing',
      value: `${money(Number(config.baseAmount ?? 0), currency)} prepaid${config.termMonths ? ` for ${config.termMonths} months` : ''}`,
    });
  } else {
    details.push({
      label: 'Billing',
      value:
        Number(config.baseAmount ?? 0) > 0
          ? `${money(Number(config.baseAmount), currency)} per completed visit`
          : 'Per completed visit',
    });
  }

  if (config.minimumVisits != null) {
    details.push({ label: 'Included visits', value: String(config.minimumVisits) });
  }
  if (config.overageRate != null && config.overageRate > 0) {
    details.push({ label: 'Additional visits', value: money(config.overageRate, currency) });
  }
  if (config.autoRenew != null) {
    details.push({
      label: 'Renewal',
      value: config.autoRenew ? 'Renews automatically' : 'Does not renew automatically',
    });
  }
  if (config.prorationPolicy) {
    const labels = {
      DAILY: 'Prorate by day',
      FULL_MONTH: 'Whole months only',
      NONE: 'No proration or refund',
    };
    details.push({ label: 'Refund for unused time', value: labels[config.prorationPolicy] });
  }
  if (config.escalation && config.escalation.type !== 'NONE') {
    const escalation =
      config.escalation.type === 'FIXED_PERCENT'
        ? `${config.escalation.value ?? 0}% annual increase`
        : config.escalation.type === 'FIXED_AMOUNT'
          ? `${money(config.escalation.value ?? 0, currency)} annual increase`
          : config.escalation.type === 'CPI_INDEXED'
            ? `Annual CPI adjustment${config.escalation.cpiIndex ? ` (${config.escalation.cpiIndex})` : ''}`
            : 'Scheduled price increases';
    details.push({ label: 'Price changes', value: escalation });
  }
  if (config.minimumTermMonths) {
    details.push({ label: 'Minimum term', value: `${config.minimumTermMonths} months` });
  }
  if (config.pauseBillingPolicy) {
    const pauseLabels = {
      CONTINUE: 'Billing continues while service is paused',
      PRORATE_CREDIT: 'Paused time is credited proportionally',
      SUSPEND: 'Billing pauses with service',
    };
    details.push({ label: 'Paused service', value: pauseLabels[config.pauseBillingPolicy] });
  }
  if (config.cancellationFeePolicy && config.cancellationFeePolicy !== 'none') {
    // The flat fee is only charged for a cancel inside the minimum term.
    const flatFee = `${money(config.cancellationFee ?? 0, currency)} cancellation fee`;
    details.push({
      label: 'Cancellation',
      value:
        config.cancellationFeePolicy === 'flat'
          ? config.minimumTermMonths
            ? `${flatFee} in the first ${config.minimumTermMonths} months`
            : flatFee
          : 'Remaining minimum-term balance is due',
    });
  }
  if (config.seasonStart && config.seasonEnd) {
    details.push({ label: 'Active season', value: `${config.seasonStart} to ${config.seasonEnd}` });
  }
  if (config.holidayPolicy) {
    const holidayLabels = {
      SKIP: 'Skip holiday visits',
      NEXT_BUSINESS_DAY: 'Move holiday visits to the next business day',
      PROCEED: 'Service holidays as scheduled',
    };
    details.push({ label: 'Holidays', value: holidayLabels[config.holidayPolicy] });
  }
  if (config.blackoutDates?.length) {
    details.push({
      label: 'Blackout dates',
      value: config.blackoutDates.map(formatRecurringDate).join(', '),
    });
  }

  return { frequency, start, duration, details };
}

export function formatRecurringSummary(config: EstimateRecurringConfig): string {
  const frequency =
    config.frequency === 'CUSTOM' && config.interval && config.intervalUnit
      ? `every ${config.interval} ${String(config.intervalUnit).toLowerCase()}`
      : (FREQUENCY_LABEL[config.frequency] ?? String(config.frequency).toLowerCase());
  const parts = [frequency];

  if (config.startDate) {
    parts.push(`starting ${formatRecurringDate(config.startDate)}`);
  }

  if (config.endType === 'AFTER_COUNT' && config.endAfterCount) {
    parts.push(`ends after ${config.endAfterCount} visits`);
  } else if (config.endType === 'ON_DATE' && config.endOnDate) {
    parts.push(`ends ${formatRecurringDate(config.endOnDate)}`);
  }

  return parts.join(' · ');
}
